
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import SingleStepEventCard from '@/components/sports/common/SingleStepEventCard.vue';
import MotorsportEventCard from '@/components/sports/motorsport/MotorsportEventCard.vue';

import {
	MultiStepSportEvent,
	ObjectType,
	SingleStepSportEvent,
	SportEventStatus,
	SportType,
} from '@/@types/graphql-types';

import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';

import type { SportEventV2, SportOffer } from '@/@types/graphql-types';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import type { WebLocale } from '@/enums/web-locale';
import type { Locale } from '@/helpers/locale-helper';
import { SingleStepContext } from '@/helpers/sports/config';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import type Provider from '@/interfaces/provider';
import type { SportEventV2Url } from '@/interfaces/sport-graphql';

const Constant = namespace('constant');
const Language = namespace('language');
const User = namespace('user');

@Component({
	name: 'EventCard',
	components: { ProviderIcon, HiddenHorizontalScrollbar },
})
export default class EventCard extends Vue {
	// offers get created with a domainSessionId in their link out url.
	// this is needed since domainSessionId is not available beforehand, but still the link needs to be created with contexts.
	// changing domainSessionId later will also trigger currentOffers anew.
	protected domainSessionId = '' as string | undefined;

	@Prop({ required: true }) event: SportEventV2Url<SingleStepSportEvent | MultiStepSportEvent>;
	@Prop() bucketIndex: number | undefined;
	@Prop() eventIndex: number | undefined;
	@Prop({ required: true }) context: SingleStepContext;

	@Constant.Getter providersById: Record<string, Provider>;

	@Language.State locales: Locale[];
	@Language.State webLocale: WebLocale;
	@Language.Getter country: string;
	@Language.Getter language: string;
	@Language.Getter locale: string;

	@User.State timeZone: string | undefined;

	expandOffers = false;

	get sportEventCard() {
		if (this.event.node.sport === SportType.FormulaRacing) {
			return MotorsportEventCard;
		}

		return SingleStepEventCard;
	}

	get offers() {
		return this.event.node.offers ?? [];
	}

	get watchNowOffer(): SportOffer | null {
		return this.event.node.watchNowOffer ?? null;
	}

	get offerCount() {
		return this.event.node.offerCount ?? null;
	}

	get eventStatus() {
		return this.event.node.content?.status;
	}

	get isLive() {
		return this.eventStatus === SportEventStatus.Live;
	}

	get isEnded() {
		return this.eventStatus === SportEventStatus.Closed;
	}

	get isCancelled() {
		return this.eventStatus === SportEventStatus.Cancelled;
	}

	get isDelayed() {
		return (
			this.eventStatus === SportEventStatus.Postponed ||
			this.eventStatus === SportEventStatus.Suspended ||
			this.eventStatus === SportEventStatus.Delayed
		);
	}

	get gameStatusContent() {
		if (this.isEnded) return this.$t('SPORTS_WEBAPP_ENDED');
		if (this.isLive) return this.$t('SPORTS_WEBAPP_LIVE');
		if (this.isCancelled) return this.$t('SPORTS_WEBAPP_CANCELLED');
		if (this.eventStatus === SportEventStatus.Postponed) return this.$t('SPORTS_WEBAPP_POSTPONED');
		if (this.eventStatus === SportEventStatus.Suspended) return this.$t('SPORTS_WEBAPP_SUSPENDED');
		if (this.eventStatus === SportEventStatus.Delayed) return this.$t('SPORTS_WEBAPP_DELAYED');
	}

	get sportContext() {
		let eventNode = this.event.node;

		if ('parent' in eventNode) {
			// MultiStepSportEvent
			return SnowplowSportContext.fromSportEventV2(this.event.node.sport, ObjectType.SportEvent, eventNode);
		}

		// Fallback if event node has no status e.g. due to status living in parent event for formula_racing
		if (!eventNode.content.status) (eventNode.content as any).status = this.eventStatus;

		return SnowplowSportContext.fromSportEventV2(this.event.node.sport, ObjectType.SportEvent, eventNode);
	}

	get sport() {
		return this.event.node.sport;
	}

	formatDate(ts: Date) {
		return formatDate(ts, this.locales, this.webLocale, this.timeZone, this.language);
	}

	formatTime(ts: Date) {
		return formatTime(ts, this.locales, this.webLocale, this.timeZone, this.language);
	}

	isSponsoredOffer(offer: SportEventV2['watchNowOffer'] | SportOffer) {
		if (offer == null) return false;
		return SponsoredClickoutsHelper.isSponsoredOfferGraphql(offer.package.packageId, this.webLocale);
	}

	getOfferClickoutUrl(offer: SportOffer) {
		return new ClickoutUrl(offer)
			.set('uct_country', this.country)
			.set('uct_buybox', BuyboxParam.Sport)
			.set('sid', this.domainSessionId)
			.set('uct_sport', this.sport)
			.toString();
	}

	handleOffersClick() {
		this.expandOffers = !this.expandOffers;

		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'more_providers_click', label: this.event.node.sport, property: 'card' },
			[this.sportContext]
		);
	}

	handleClickOut(offer: SportOffer | null) {
		if (offer == null) return;
		trackClickOutSports(offer, this.bucketIndex, this.eventIndex, offer.sportObjectType, 'card', [
			this.sportContext,
		]);
	}

	handleEventClick() {
		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'event_clicked', label: this.context.pageType, property: 'card', useLastRoute: true },
			[this.sportContext]
		);
	}
}
